"use client";

import { useState, useEffect } from "react";
import { KiteModel, KiteModelRequestDTO } from "@/common/model/KiteModel";
import { KiteModelService } from "@/services/KiteModelService";
import { GearType, GearTypeService } from "@/services/GearTypeService";
import GearItemsWithDialog from "./GearItemsWithDialog";

// Helper function to get display name for gear type based on ID
function getGearTypeDisplayName(
  gearTypeId: string | number,
  gearTypes: GearType[]
): string {
  if (!gearTypeId || !gearTypes.length) return "Not set";
  const id = typeof gearTypeId === "string" ? parseInt(gearTypeId) : gearTypeId;
  const gearType = gearTypes.find((type) => type.id === id);
  return gearType ? gearType.name : "Not set";
}

interface KiteModelsProps {
  initialSearch?: string;
  onSearchChange?: (search: string) => void;
  initialPage?: number;
  onPageChange?: (page: number) => void;
  initialLimit?: number;
  onLimitChange?: (limit: number) => void;
}

const KiteModels = ({
  initialSearch = "",
  onSearchChange,
  initialPage = 1,
  onPageChange,
  initialLimit = 5,
  onLimitChange,
}: KiteModelsProps = {}) => {
  const [gearTypes, setGearTypes] = useState<GearType[]>([]);
  const [isLoadingGearTypes, setIsLoadingGearTypes] = useState<boolean>(true);

  // Fetch gear types from the backend on component mount
  useEffect(() => {
    const fetchGearTypes = async () => {
      try {
        setIsLoadingGearTypes(true);
        const types = await GearTypeService.getAllGearTypes();
        setGearTypes(types);
      } catch (error) {
        console.error("Error fetching gear types:", error);
      } finally {
        setIsLoadingGearTypes(false);
      }
    };

    fetchGearTypes();
  }, []);
  // Define columns for the models table
  const columns = [
    {
      field: "model",
      header: "Model Name",
      sortable: true,
    },
    {
      field: "kitebrandname",
      header: "Brand",
      sortable: true,
    },
    {
      field: "geartypename",
      header: "Gear Type",
      sortable: true,
      render: (item: KiteModel) =>
        item.gear_type_id
          ? getGearTypeDisplayName(item.gear_type_id, gearTypes)
          : "Not set",
    },
  ];

  // Define form fields for adding/editing models
  const formFields = [
    {
      name: "model",
      label: "Model Name",
      type: "text" as const,
      required: true,
    },
    {
      name: "kitebrandid",
      label: "Brand",
      type: "brand-search" as const,
      required: true,
    },
    {
      name: "gear_type_id",
      label: "Gear Type",
      type: "select" as const,
      required: true,
      options: isLoadingGearTypes
        ? [{ value: "", label: "Loading..." }]
        : gearTypes.map((type) => ({
            value: type.id.toString(),
            label: type.name,
          })),
    },
  ];

  // Function to create RequestDTO from form data
  const createModelRequestDto = (
    formData: Record<string, any>
  ): KiteModelRequestDTO => {
    return {
      model: formData.model,
      kitebrandid: Number(formData.kitebrandid),
      // Si no se proporciona un gear_type_id, usamos el primer tipo disponible o 1 como valor predeterminado
      gear_type_id: formData.gear_type_id
        ? Number(formData.gear_type_id)
        : gearTypes.length > 0
        ? gearTypes[0].id
        : 1,
    };
  };

  return (
    <GearItemsWithDialog<KiteModel, KiteModelRequestDTO>
      title="Manage Models"
      service={{
        getItems: (limit, sortField, sortOrder, page, search) =>
          KiteModelService.getModels(
            limit,
            sortField as "model" | "kitebrandname" | "geartypename",
            sortOrder as "asc" | "desc",
            page,
            search
          ),
        createItem: (data) => KiteModelService.createModel(data) as any,
        updateItem: (id, data) => KiteModelService.updateModel(id, data) as any,
        deleteItem: (id) => KiteModelService.deleteModel(id),
      }}
      columns={columns}
      formFields={formFields}
      itemTypeNameSingular="Model"
      itemTypeNamePlural="models"
      requestDtoFactory={createModelRequestDto}
      initialSearch={initialSearch}
      onSearchChange={onSearchChange}
      initialPage={initialPage}
      onPageChange={onPageChange}
      initialLimit={initialLimit}
      onLimitChange={onLimitChange}
    />
  );
};

export default KiteModels;
